import React, { useState } from 'react';
import { Box, Flex, Spinner, Text, VStack } from '@chakra-ui/react';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { useNavigate } from 'react-router-dom';

import { loginWithGoogle } from '@/api/authApi/authApi';
import { useAuth } from '@/context/AuthContext';
import { isMicrosoftSsoConfigured } from '@/config';
import { ErrorBlock } from '@/components/ErrorBlock';
import { MicrosoftSignInButton } from './MicrosoftSignInButton';

export const SsoSignIn = () => {
    const { handleSsoLogin } = useAuth();
    const navigate = useNavigate();
    const [error, setError] = useState<string | undefined>();
    const [isSigningIn, setIsSigningIn] = useState(false);
    const showMicrosoft = isMicrosoftSsoConfigured();

    const handleAuthError = (err: any) => {
        setIsSigningIn(false);

        if (err?.status === 403 || err?.statusCode === 403) {
            navigate('/unauthorized');
            return;
        }

        setError(err?.message || 'Sign in failed. Please try again.');
    };

    const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
        setError(undefined);

        if (!credentialResponse.credential) {
            setError('Google sign in failed. Please try again.');
            return;
        }

        setIsSigningIn(true);

        try {
            const response = await loginWithGoogle(credentialResponse.credential);
            await handleSsoLogin(response.token);
        } catch (err: any) {
            handleAuthError(err);
        }
    };

    if (isSigningIn) {
        return (
            <Flex
                direction="column"
                align="center"
                justify="center"
                py={10}
                gap={4}
                aria-busy="true"
                aria-live="polite"
            >
                <Spinner
                    thickness="3px"
                    speed="0.65s"
                    emptyColor="gray.200"
                    color="brand.blue"
                    size="lg"
                />
                <Text color="#5D697C" fontSize="md" fontWeight={500}>
                    Signing you in…
                </Text>
                <Text color="#A0AEC0" fontSize="sm" textAlign="center">
                    Please wait while we verify your account
                </Text>
            </Flex>
        );
    }

    return (
        <VStack spacing={5} align="center" w="full">
            <Box w="full" display="flex" justifyContent="center">
                <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={() => setError('Google sign in failed. Please try again.')}
                    text="signin_with"
                    shape="rectangular"
                    size="large"
                    width="320"
                    theme="outline"
                />
            </Box>

            {showMicrosoft && (
                <>
                    <Text textAlign="center" fontSize="sm" color="gray.400" w="full">
                        or
                    </Text>
                    <Box w="full" maxW="320px">
                        <MicrosoftSignInButton
                            onAuthError={handleAuthError}
                            onSigningIn={() => {
                                setError(undefined);
                                setIsSigningIn(true);
                            }}
                        />
                    </Box>
                    <Text textAlign="center" fontSize="xs" color="gray.400" maxW="320px">
                        Use Microsoft if Google is unavailable (e.g. in China)
                    </Text>
                </>
            )}

            {error && (
                <Box w="full" textAlign="center">
                    <ErrorBlock textOnly error={error} />
                </Box>
            )}
        </VStack>
    );
};
